import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useShop } from "../contexts/ShopContext";
import { useAuth } from "../contexts/AuthContext";
import SortableTable from "../components/SortableTable";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import Receipt from "../components/Receipt";
import { RotateCcw, Eye, Printer, Pencil, Check, X, Download } from "lucide-react";
import { toast } from "sonner";
import { printReceipt, downloadReceiptPdf } from "../lib/printReceipt";

export default function Returns() {
  const { shops } = useShop();
  const { user } = useAuth();
  const [bills, setBills] = useState([]);
  const [shopFilter, setShopFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewBill, setViewBill] = useState(null);
  const [refundQty, setRefundQty] = useState({}); // {pid: qty}
  const [editMode, setEditMode] = useState(false);
  const [editItems, setEditItems] = useState([]); // [{product_id, name, qty, rate, mrp}]

  const load = async () => {
    const url = shopFilter === "all" ? "/bills?limit=500" : `/bills?shop_id=${shopFilter}&limit=500`;
    const r = await api.get(url);
    setBills(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [shopFilter]);

  const filtered = bills.filter((b) => statusFilter === "all" || b.status === statusFilter);

  const refundFull = async (id) => {
    if (!window.confirm("Refund the ENTIRE bill? Stock will be restored.")) return;
    try { await api.post(`/bills/${id}/refund`); toast.success("Bill refunded"); setViewBill(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
  };

  const refundPartial = async () => {
    if (!viewBill) return;
    const items = Object.entries(refundQty)
      .map(([pid, q]) => ({ product_id: pid, qty: Math.max(0, Number(q) || 0) }))
      .filter((x) => x.qty > 0);
    if (items.length === 0) { toast.error("Set qty > 0 for at least 1 item"); return; }
    try {
      const r = await api.post(`/bills/${viewBill.bill_id}/refund-items`, { items });
      toast.success(`Refunded ₹${Math.round(r.data.refunded_value)} (status: ${r.data.status})`);
      setRefundQty({});
      setViewBill(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Partial refund failed");
    }
  };

  const doPrint = () => {
    if (!viewBill) { toast.error("Open the bill first"); return; }
    // Receipt is already mounted inside the dialog; wait a tick for QR canvas to render
    setTimeout(() => {
      const shop = shops.find((s) => s.shop_id === viewBill.shop_id) || shops[0] || {};
      printReceipt(viewBill, shop, "returns-receipt-preview");
    }, 100);
  };

  const doDownloadPdf = () => {
    if (!viewBill) { toast.error("Open the bill first"); return; }
    setTimeout(() => downloadReceiptPdf(viewBill, "returns-receipt-preview"), 100);
  };

  // Quick print (row action) — open dialog then print
  const printFromRow = (bill) => {
    setViewBill(bill);
    setRefundQty({});
    // Wait for dialog + Receipt to mount, then print
    setTimeout(() => {
      const shop = shops.find((s) => s.shop_id === bill.shop_id) || shops[0] || {};
      printReceipt(bill, shop, "returns-receipt-preview");
    }, 500);
  };

  const startEdit = () => {
    if (!viewBill) return;
    setEditItems(viewBill.items.map((it) => ({
      product_id: it.product_id, name: it.name,
      qty: Number(it.qty), rate: Number(it.rate), mrp: Number(it.mrp || 0),
    })));
    setEditMode(true);
  };

  const saveEdit = async () => {
    if (!viewBill) return;
    // Basic validation
    const clean = editItems
      .map((it) => ({ ...it, qty: Number(it.qty) || 0, rate: Number(it.rate) || 0, mrp: Number(it.mrp) || 0 }))
      .filter((it) => it.qty > 0);
    if (clean.length === 0) { toast.error("At least one item required"); return; }
    try {
      const r = await api.put(`/bills/${viewBill.bill_id}`, { items: clean });
      toast.success(`Bill updated · New total ₹${Math.round(r.data.net_total)}`);
      setViewBill(r.data);
      setEditMode(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Edit failed");
    }
  };

  const editSubtotal = editItems.reduce((s, x) => s + Number(x.rate || 0) * Number(x.qty || 0), 0);
  const editNet = Math.max(0, Math.round(editSubtotal - Number(viewBill?.discount || 0)));

  const cols = [
    { key: "bill_no", label: "Bill No", render: (r) => <span className="font-mono text-xs">{r.bill_no}</span> },
    { key: "created_at", label: "Date", accessor: (r) => r.created_at, render: (r) => <span className="text-xs">{new Date(r.created_at).toLocaleString()}</span> },
    { key: "shop_id", label: "Shop", render: (r) => <Badge variant="outline" className="text-xs">{r.shop_id}</Badge> },
    { key: "customer_name", label: "Customer", render: (r) => <span>{r.customer_name || "-"} <span className="text-xs text-muted-foreground">{r.customer_phone}</span></span> },
    { key: "items", label: "Items", align: "right", accessor: (r) => r.items.length, render: (r) => <span className="text-xs">{r.items.length}</span> },
    { key: "payment_method", label: "Payment", render: (r) => <span className="uppercase text-xs">{r.payment_method}</span> },
    { key: "created_by_name", label: "By", render: (r) => <span className="text-xs">{r.created_by_name || "-"}</span> },
    { key: "status", label: "Status", render: (r) => (
      <Badge
        variant={r.status === "refunded" ? "destructive" : "outline"}
        className={r.status === "completed" ? "border-moss text-moss" : (r.status === "partial_refunded" ? "border-ochre text-ochre" : "")}
      >{r.status}</Badge>
    ) },
    { key: "net_total", label: "Total", align: "right", accessor: (r) => r.net_total, render: (r) => <span className="font-mono font-bold">₹{Math.round(r.net_total)}</span> },
    { key: "_action", label: "Actions", sortable: false, render: (r) => (
      <div className="flex gap-1">
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setViewBill(r); setRefundQty({}); }} data-testid={`view-${r.bill_id}`} className="gap-1">
          <Eye size={12}/>View
        </Button>
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); printFromRow(r); }} data-testid={`print-${r.bill_id}`} className="gap-1">
          <Printer size={12}/>Print
        </Button>
      </div>
    ) },
  ];

  // Compute remaining qty per item in viewBill
  const remainingQty = (it) => {
    if (!viewBill) return 0;
    const refunded = (viewBill.items_refunded || []).filter((r) => r.product_id === it.product_id).reduce((s, r) => s + Number(r.qty || 0), 0);
    return Math.max(0, Number(it.qty) - refunded);
  };

  return (
    <div className="p-8 overflow-y-auto" data-testid="returns-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-overline text-muted-foreground">Bills &amp; Refunds</div>
          <h1 className="font-heading text-4xl font-bold text-ink">Bills / Returns</h1>
          <p className="text-sm text-muted-foreground mt-1">View, reprint, or refund (full / item-level) any bill.</p>
        </div>
        <div className="flex gap-3">
          <div className="w-44">
            <Select value={shopFilter} onValueChange={setShopFilter}>
              <SelectTrigger data-testid="returns-shop-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Shops</SelectItem>
                {shops.map((s) => <SelectItem key={s.shop_id} value={s.shop_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="returns-status-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="partial_refunded">Partial Refund</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <SortableTable
        testid="returns-table"
        columns={cols}
        data={filtered}
        searchable={["bill_no", "customer_name", "customer_phone"]}
        initialSort={{ key: "created_at", dir: "desc" }}
        emptyText="No bills found."
      />

      <Dialog open={!!viewBill} onOpenChange={(o) => { if (!o) { setViewBill(null); setRefundQty({}); setEditMode(false); } }}>
        <DialogContent className="max-w-2xl" data-testid="bill-view-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Bill {viewBill?.bill_no} {editMode && <span className="text-sm text-ochre">· editing</span>}</DialogTitle>
          </DialogHeader>
          {viewBill && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-sm bg-sand p-3 rounded">
                <div><div className="label-overline text-muted-foreground">Date</div>{new Date(viewBill.created_at).toLocaleString()}</div>
                <div><div className="label-overline text-muted-foreground">Customer</div>{viewBill.customer_name || "-"} · {viewBill.customer_phone || "-"}</div>
                <div><div className="label-overline text-muted-foreground">Status</div><Badge>{viewBill.status}</Badge></div>
              </div>
              {/* Hidden mount for shared print flow — same DOM as POS Receipt */}
              <div id="returns-receipt-preview" style={{ position: "absolute", left: "-99999px", top: 0 }}>
                <Receipt bill={viewBill} shop={shops.find((s) => s.shop_id === viewBill.shop_id) || shops[0] || {}} />
              </div>
              {editMode ? (
                <div className="border border-ochre rounded max-h-72 overflow-y-auto scroll-thin" data-testid="edit-mode-panel">
                  <table className="w-full text-sm">
                    <thead className="bg-ochre/10 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">Product</th>
                        <th className="text-right px-2 py-1">Qty</th>
                        <th className="text-right px-2 py-1">MRP</th>
                        <th className="text-right px-2 py-1">Rate</th>
                        <th className="text-right px-2 py-1">Amount</th>
                        <th className="px-2 py-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editItems.map((it, i) => (
                        <tr key={it.product_id} className="border-t border-border">
                          <td className="px-2 py-1">{it.name}</td>
                          <td className="text-right px-2 py-1">
                            <Input
                              type="number" min="0" value={it.qty}
                              onChange={(e) => {
                                const n = [...editItems]; n[i] = { ...n[i], qty: Number(e.target.value) || 0 }; setEditItems(n);
                              }}
                              className="w-16 h-7 text-right font-mono ml-auto"
                              data-testid={`edit-qty-${it.product_id}`}
                            />
                          </td>
                          <td className="text-right px-2 py-1">
                            <Input
                              type="number" min="0" value={it.mrp}
                              onChange={(e) => {
                                const n = [...editItems]; n[i] = { ...n[i], mrp: Number(e.target.value) || 0 }; setEditItems(n);
                              }}
                              className="w-20 h-7 text-right font-mono ml-auto"
                              data-testid={`edit-mrp-${it.product_id}`}
                            />
                          </td>
                          <td className="text-right px-2 py-1">
                            <Input
                              type="number" min="0" value={it.rate}
                              onChange={(e) => {
                                const n = [...editItems]; n[i] = { ...n[i], rate: Number(e.target.value) || 0 }; setEditItems(n);
                              }}
                              className="w-20 h-7 text-right font-mono ml-auto"
                              data-testid={`edit-rate-${it.product_id}`}
                            />
                          </td>
                          <td className="text-right font-mono font-bold px-2 py-1">₹{Math.round(it.rate * it.qty)}</td>
                          <td className="px-2 py-1">
                            <button
                              onClick={() => setEditItems(editItems.filter((_, idx) => idx !== i))}
                              className="text-destructive hover:opacity-70"
                              data-testid={`edit-remove-${it.product_id}`}
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
              <div className="border border-border rounded max-h-72 overflow-y-auto scroll-thin">
                <table className="w-full text-sm">
                  <thead className="bg-surface-alt sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1">Product</th>
                      <th className="text-right px-2 py-1">Rate</th>
                      <th className="text-right px-2 py-1">Sold Qty</th>
                      <th className="text-right px-2 py-1">Already Refunded</th>
                      {viewBill.status !== "refunded" && (
                        <th className="text-right px-2 py-1">Refund Qty</th>
                      )}
                      <th className="text-right px-2 py-1">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewBill.items.map((it) => {
                      const rem = remainingQty(it);
                      const alreadyRef = Number(it.qty) - rem;
                      return (
                        <tr key={it.product_id} className="border-t border-border">
                          <td className="px-2 py-1">{it.name}</td>
                          <td className="text-right font-mono px-2 py-1">₹{Math.round(it.rate)}</td>
                          <td className="text-right px-2 py-1">{it.qty}</td>
                          <td className="text-right text-destructive font-mono px-2 py-1">{alreadyRef || 0}</td>
                          {viewBill.status !== "refunded" && (
                            <td className="text-right px-2 py-1">
                              <Input
                                type="number"
                                min="0"
                                max={rem}
                                value={refundQty[it.product_id] ?? ""}
                                onChange={(e) => setRefundQty({ ...refundQty, [it.product_id]: e.target.value })}
                                disabled={rem === 0}
                                placeholder={rem === 0 ? "—" : `0/${rem}`}
                                className="w-16 h-7 text-right font-mono ml-auto"
                                data-testid={`refund-qty-${it.product_id}`}
                              />
                            </td>
                          )}
                          <td className="text-right font-mono px-2 py-1">₹{Math.round(it.amount)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
              <div className="flex justify-between border-t border-border pt-2">
                <span className="font-heading font-bold">Net Total</span>
                <span className="font-mono text-xl font-bold text-terracotta">
                  ₹{Math.round(editMode ? editNet : viewBill.net_total)}
                  {editMode && Math.round(editNet) !== Math.round(viewBill.net_total) && (
                    <span className="ml-2 text-xs text-muted-foreground line-through">₹{Math.round(viewBill.net_total)}</span>
                  )}
                </span>
              </div>
              {viewBill.refunded_value > 0 && (
                <div className="flex justify-between text-sm text-destructive">
                  <span>Already Refunded</span>
                  <span className="font-mono font-bold">-₹{Math.round(viewBill.refunded_value)}</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {editMode ? (
              <>
                <Button variant="outline" onClick={() => setEditMode(false)} data-testid="edit-cancel-btn" className="gap-2"><X size={14}/>Cancel</Button>
                <Button onClick={saveEdit} data-testid="edit-save-btn" className="bg-ochre hover:bg-ochre/90 text-white gap-2"><Check size={14}/>Save &amp; Recalc</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={doPrint} data-testid="dialog-print-btn" className="gap-2">
                  <Printer size={14}/>Print
                </Button>
                <Button variant="outline" onClick={doDownloadPdf} data-testid="dialog-pdf-btn" className="gap-2">
                  <Download size={14}/>PDF
                </Button>
                {user?.role === "admin" && viewBill && viewBill.status === "completed" && (
                  <Button variant="outline" onClick={startEdit} data-testid="edit-bill-btn" className="gap-2 border-ochre text-ochre hover:bg-ochre/10">
                    <Pencil size={14}/>Edit Bill
                  </Button>
                )}
                {viewBill && viewBill.status !== "refunded" && (
                  <>
                    <Button onClick={refundPartial} data-testid="dialog-partial-refund-btn" className="bg-ochre text-white gap-2 hover:bg-ochre/90">
                      <RotateCcw size={14}/>Refund Selected Items
                    </Button>
                    <Button onClick={() => refundFull(viewBill.bill_id)} data-testid="dialog-full-refund-btn" variant="destructive" className="gap-2">
                      <RotateCcw size={14}/>Refund Full Bill
                    </Button>
                  </>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
